/**
 * Mailing list submission + session tracking.
 *
 * Single source of truth for both the timed popup and the footer signup
 * section. Handles:
 *  - per-session UUID (sessionStorage)
 *  - duplicate-submit guard (sessionStorage flag)
 *  - email + phone validation
 *  - POST to backend proxy → forwards to Boost.space webhook
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_ID_KEY = "btk_ml_session_id";
const SUBMITTED_KEY = "btk_ml_submitted"; // sessionStorage — prevents resubmits
const POPUP_DISMISSED_KEY = "btk_ml_popup_dismissed"; // sessionStorage — popup once per session

const safeWindow = () => (typeof window !== "undefined" ? window : null);

/* ---------- Session ID ---------- */
export const getSessionId = () => {
  const w = safeWindow();
  if (!w) return null;
  try {
    let id = w.sessionStorage.getItem(SESSION_ID_KEY);
    if (id) return id;
    // Use crypto.randomUUID when available, fall back to a v4-like string
    if (typeof w.crypto?.randomUUID === "function") {
      id = w.crypto.randomUUID();
    } else {
      id = "btk-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
    }
    w.sessionStorage.setItem(SESSION_ID_KEY, id);
    return id;
  } catch (_) {
    return null;
  }
};

/* ---------- Submission state (per session) ---------- */
export const hasSubmittedThisSession = () => {
  const w = safeWindow();
  if (!w) return false;
  try { return w.sessionStorage.getItem(SUBMITTED_KEY) === "1"; }
  catch (_) { return false; }
};

const markSubmitted = () => {
  const w = safeWindow();
  if (!w) return;
  try { w.sessionStorage.setItem(SUBMITTED_KEY, "1"); } catch (_) {}
};

/* ---------- Popup dismissal state (per session) ---------- */
export const popupDismissedThisSession = () => {
  const w = safeWindow();
  if (!w) return false;
  try {
    return w.sessionStorage.getItem(POPUP_DISMISSED_KEY) === "1" || hasSubmittedThisSession();
  } catch (_) { return false; }
};

export const markPopupDismissed = () => {
  const w = safeWindow();
  if (!w) return;
  try { w.sessionStorage.setItem(POPUP_DISMISSED_KEY, "1"); } catch (_) {}
};

/* ---------- Validation ---------- */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (v) => typeof v === "string" && EMAIL_RE.test(v.trim());

/** Loose phone validation — at least 7 digits after stripping non-digits. */
export const isValidPhone = (v) => {
  if (typeof v !== "string") return false;
  return v.replace(/\D/g, "").length >= 7;
};

/**
 * Validate a form payload before sending. Returns { ok, error } where error is
 * a single user-facing message keyed for the offending field.
 */
export const validateSubmission = ({ email, wantsSms, phone, emailMarketingOptIn, smsOptIn }) => {
  if (!isValidEmail(email)) return { ok: false, error: "Please enter a valid email address." };
  if (!emailMarketingOptIn) return { ok: false, error: "Please confirm the email marketing opt-in to subscribe." };
  if (wantsSms) {
    if (!isValidPhone(phone || "")) return { ok: false, error: "Please enter a valid phone number for SMS notifications." };
    if (!smsOptIn) return { ok: false, error: "Please confirm the SMS opt-in to receive text messages." };
  }
  return { ok: true };
};

/* ---------- Submit ---------- */

/**
 * Submit mailing-list signup. Source must be "timed_popup" | "footer_signup".
 * Returns the response data on success; throws on validation or network error.
 */
export const submitMailingList = async (form, source) => {
  const v = validateSubmission(form);
  if (!v.ok) throw new Error(v.error);

  const w = safeWindow();
  const payload = {
    source,
    email: (form.email || "").trim().toLowerCase(),
    firstName: (form.firstName || "").trim() || null,
    lastName: (form.lastName || "").trim() || null,
    company: (form.company || "").trim() || null,
    interests: Array.isArray(form.interests) ? form.interests : [],
    wantsSms: !!form.wantsSms,
    phone: form.wantsSms ? (form.phone || "").trim() : null,
    emailMarketingOptIn: true,
    smsOptIn: !!(form.wantsSms && form.smsOptIn),
    pageUrl: w ? w.location.href : null,
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    sessionId: getSessionId(),
  };

  const res = await axios.post(`${API}/mailing-list/subscribe`, payload, { timeout: 15000 });
  markSubmitted();
  return res.data;
};
